import { useNavigate } from "@tanstack/react-router"
import { Button } from "design-system/atoms/Button"
import { cn } from "design-system/utils"
import { Plus, RotateCw } from "lucide-react"
import { useRefreshWhatsappTemplatesMutation } from "modules/campaigns/hooks/sections/whatsapp/useRefreshWhatsappTemplatesMutation"
import { useWhatsappTemplatesQuery } from "modules/campaigns/hooks/sections/whatsapp/useWhatsappTemplatesQuery"
import { WhatsappTabs } from "modules/settings/components/integrations/whatsapp/WhatsappTabs"
import { Pagination } from "modules/shared/components/Pagination"
import { usePagination } from "modules/shared/hooks/usePagination"
import { useSortBy } from "modules/shared/hooks/useSortBy"
import { useTranslation } from "react-i18next"
import { Route as WhatsappTemplatesRoute } from "routes/_base/_auth/settings/channels/whatsapp/templates"
import { WhatsappTemplatesSortSchema } from "schema"
import { z } from "zod"
import { WhatsappTemplatesFilter } from "./WhatsappTemplatesFilter"
import { WhatsappTemplatesTable } from "./WhatsappTemplatesTable"

type WhatsappTemplatesSortFields = z.infer<typeof WhatsappTemplatesSortSchema>["sortBy"]["field"]

const ITEMS_PER_PAGE = 30

export const WhatsappTemplates = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const search = WhatsappTemplatesRoute.useSearch()
  const { page, totalPages, setPage, setCount, count } = usePagination({
    search,
    fullPath: WhatsappTemplatesRoute.fullPath,
    total: ITEMS_PER_PAGE,
  })

  const { onSort, sortBy, fields } = useSortBy<WhatsappTemplatesSortFields[]>({
    search,
    fullPath: WhatsappTemplatesRoute.fullPath,
    fields: ["name", "status", "language"],
  })

  const { data, isPending } = useWhatsappTemplatesQuery({
    query: {
      query: search.query,
      page,
      total: ITEMS_PER_PAGE,
      sortBy,
      filterBy: search.filterBy,
    },
    onSuccess: (data) => setCount(data.pageInfo.count),
  })

  const { isPending: isRefreshing, mutate } = useRefreshWhatsappTemplatesMutation()
  const handleCreateTemplateButton = () => {
    navigate({ to: "/settings/channels/whatsapp/templates/create" })
  }

  return (
    <>
      <WhatsappTabs />
      <div className="flex flex-col gap-2">
        <WhatsappTemplatesFilter
          trailing={
            <>
              <Button
                variant="outline"
                size="icon"
                onClick={() => mutate()}
                disabled={isRefreshing}
              >
                <RotateCw className={cn(isPending && "animate-spin")} />
              </Button>
              <Button variant="default" onClick={handleCreateTemplateButton}>
                <Plus className="size-4" strokeWidth={2} />
                <span>{t("campaigns.templates.whatsapp.actions.create")}</span>
              </Button>
            </>
          }
        />
        <div className="flex flex-col gap-4">
          <WhatsappTemplatesTable
            data={data}
            isPending={isPending}
            fields={fields}
            onSort={onSort}
            search={search}
          />
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setPage}
            count={count}
          />
        </div>
      </div>
    </>
  )
}
